import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Card from '../../../../../ui/canon/Card';

const mapWorkflowToStage = (workflowState) => {
  switch (workflowState) {
    case 'invited':
      return 'invited';
    case 'reviewing_proposal':
      return 'proposal';
    case 'shortlisted':
      return 'shortlisted';
    case 'out_for_receiving_signature':
    case 'awaiting_disclosing_signature':
    case 'nda_completed':
      return 'nda';
    case 'negotiating':
      return 'negotiating';
    default:
      return 'invited';
  }
};

const BuyerProgressTracker = ({ workflowState, dealInterest, deal, company, onBackToDeals }) => {
  const intl = useIntl();

  const STAGES = [
    { id: 'invited', label: intl.formatMessage({ id: 'succession.buyer_room.progress_tracker.stage_invited' }) },
    { id: 'proposal', label: intl.formatMessage({ id: 'succession.buyer_room.progress_tracker.stage_proposal' }) },
    { id: 'shortlisted', label: intl.formatMessage({ id: 'succession.buyer_room.progress_tracker.stage_shortlisted' }) },
    { id: 'nda', label: intl.formatMessage({ id: 'succession.buyer_room.progress_tracker.stage_nda' }) },
    { id: 'negotiating', label: intl.formatMessage({ id: 'succession.buyer_room.progress_tracker.stage_negotiating' }) },
  ];

  const currentStage = mapWorkflowToStage(workflowState);
  const currentIndex = STAGES.findIndex((stage) => stage.id === currentStage);
  const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
  const progressTitle = intl.formatMessage(
    { id: 'succession.buyer_room.progress_tracker.progress_title' },
    { dealWord: company?.deal_room_single_word || 'Deal' }
  );

  return (
    <Card size="lg" className="p-8 mx-6">
      <div className="space-y-6">
        <div className="pb-4 border-b border-[var(--succession-border)]">
          <div className="flex items-start gap-3">
            {onBackToDeals && (
              <button
                type="button"
                onClick={onBackToDeals}
                className="mt-1 text-[var(--succession-text-secondary)] hover:text-[var(--succession-text-primary)] transition-colors"
                aria-label={intl.formatMessage({ id: 'succession.buyer_room.progress_tracker.back_to_deals_aria_label' })}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h3 className="text-2xl font-bold text-[var(--succession-text-primary)]">
              {deal?.headline || dealInterest.deal?.headline || progressTitle}
            </h3>
          </div>
          <p className="text-sm text-[var(--succession-text-secondary)] mt-1">
            #{deal?.deal_name || dealInterest.deal?.deal_name || dealInterest.deal_name || dealInterest.id}
          </p>
        </div>

        <h4 className="text-3xl font-thin text-[var(--succession-text-primary)]">{progressTitle}</h4>

        <div className="w-full">
          <div className="relative">
            <div className="relative flex items-start justify-between">
              {STAGES.map((stage, index) => {
                const isCompleted = index < safeCurrentIndex;
                const isCurrent = index === safeCurrentIndex;
                const isActive = isCompleted || isCurrent;
                const isConnectorCompleted = index < safeCurrentIndex;

                return (
                  <div
                    key={stage.id}
                    className="relative flex flex-col items-center"
                    style={{ width: `${100 / STAGES.length}%` }}
                  >
                    {index < STAGES.length - 1 && (
                      <div
                        className="absolute top-5 left-1/2 h-0.5 w-full transition-all duration-500 z-0"
                        style={{
                          backgroundColor: isConnectorCompleted
                            ? 'var(--succession-primary, #5489B6)'
                            : 'var(--succession-border)'
                        }}
                      />
                    )}

                    <div
                      className={`relative z-10 w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                        isActive
                          ? 'text-white border-transparent'
                          : 'text-[var(--succession-text-muted)] border-[var(--succession-border)]'
                      }`}
                      style={
                        isActive
                          ? {
                              backgroundColor: 'var(--succession-primary, #5489B6)',
                              borderColor: 'var(--succession-primary, #5489B6)',
                              boxShadow: isCurrent ? '0 8px 20px rgba(var(--succession-primary-rgb, 84, 137, 182), 0.3)' : 'none'
                            }
                          : {
                              backgroundColor: 'var(--succession-bg-primary, #ffffff)'
                            }
                      }
                    >
                      {isCompleted ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </div>

                    <div
                      className={`mt-3 text-sm font-medium text-center transition-colors ${
                        isActive ? 'text-[var(--succession-text-primary)]' : 'text-[var(--succession-text-muted)]'
                      }`}
                    >
                      {stage.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-[var(--succession-text-secondary)]">
            {intl.formatMessage({ id: 'succession.buyer_room.progress_tracker.current_stage_label' })}{' '}
            <span className="font-semibold text-[var(--succession-text-primary)]">
              {STAGES[safeCurrentIndex].label}
            </span>
          </div>
        </div>

      </div>
    </Card>
  );
};

BuyerProgressTracker.propTypes = {
  workflowState: PropTypes.string.isRequired,
  dealInterest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deal_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deal: PropTypes.shape({
      deal_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      headline: PropTypes.string
    })
  }).isRequired,
  deal: PropTypes.shape({
    deal_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    headline: PropTypes.string
  }),
  company: PropTypes.shape({
    deal_room_single_word: PropTypes.string
  }),
  onBackToDeals: PropTypes.func
};

export default BuyerProgressTracker;
